import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "../../../component/commonCard/style.module.css";
import KpiCard from "../../../component/commonCard/parts/KpiCard";
import BreakdownDonut from "../../../component/commonCard/parts/BreakdownDonut";
import BreakdownBars from "../../../component/commonCard/parts/BreakdownBars";
import FunnelChart from "../../../component/commonCard/parts/FunnelChart";
import ExportButtons from "../ExportButtons";
import { TEAL, COPPER, GREEN, MAGENTA } from "../../../component/commonCard/parts/theme";
import { getUserAnalytics } from "../../../api/HttpApis";
import { showToast } from "../../../utils/Toastify/Toastify";

const UsersSection = () => {
  const [searchParams] = useSearchParams();
  // Only `platform` is honored by the backend for this endpoint today:
  // new/returning is a fixed 30-day window and the funnel is all-time, so the
  // labels below say so. from/to aren't sent until the backend scopes by them
  // -- once it does, pass them to getUserAnalytics and drop the "(30d)" /
  // "(all-time)" qualifiers. Content type has no sensible scoping here
  // (whole-app activity, same reasoning as Overview's KPIs).
  const platform = searchParams.get("platform") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await getUserAnalytics({ platform });
        if (!cancelled) setData(res?.data?.data || null);
      } catch (err) {
        if (!cancelled) showToast(err?.response?.data?.message || "Failed to load user analytics", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [platform]);

  if (loading) return <div className={styled.loading}>Loading user analytics…</div>;

  const nvr = data?.new_vs_returning || {};
  const funnel = data?.engagement_funnel || {};
  // Computed server-side (AnalyticsService.userAnalytics): share of Details
  // page views followed by a play from that page. plays / screen_views isn't a
  // conversion -- many plays never pass through the Details page. Until the
  // backend sends conversion_percent, show "—" rather than a made-up 0.0%.
  const conversionPct = funnel.conversion_percent == null ? null : Number(funnel.conversion_percent);
  const hasConversion = conversionPct != null && Number.isFinite(conversionPct);

  const exportRows = [
    { Metric: "New Users (30d)", Value: nvr.new || 0 },
    { Metric: "Returning Users (30d)", Value: nvr.returning || 0 },
    { Metric: "Details Page Views (all-time)", Value: funnel.screen_views || 0 },
    { Metric: "View → Play Conversion (%, all-time)", Value: hasConversion ? conversionPct : "" },
    { Metric: "Video Starts (all-time)", Value: funnel.plays || 0 },
    { Metric: "Video Completions (all-time)", Value: funnel.completes || 0 },
    ...(data?.device_breakdown || []).map((d) => ({ Metric: `Views on ${d.device} (all-time)`, Value: d.count })),
  ];

  return (
    <>
      <ExportButtons rows={exportRows} filename="user-analytics" />

      <div className={styled.kpiRow}>
        <div title="Users whose first-ever event was in the last 30 days.">
          <KpiCard label="New Users (30d)" value={(nvr.new || 0).toLocaleString()} accent={COPPER} />
        </div>
        <div title="Users active in the last 30 days who were first seen earlier.">
          <KpiCard label="Returning Users (30d)" value={(nvr.returning || 0).toLocaleString()} accent={TEAL} />
        </div>
        <div title="Share of title Details page views where the viewer started playing from that page, all-time.">
          <KpiCard label="View → Play Conversion (all-time)" value={hasConversion ? `${conversionPct.toFixed(1)}%` : "—"} accent={MAGENTA} />
        </div>
      </div>

      <div className={styled.chartsRow}>
        <BreakdownDonut
          title="New vs Returning (30d)"
          subtitle="New = here for the first time. Returning = been here before."
          // Same colors as the New (copper) / Returning (teal) KPI cards above.
          rows={[
            { label: "New", value: nvr.new, color: COPPER },
            { label: "Returning", value: nvr.returning, color: TEAL },
          ]}
          nameKey="label"
          valueKey="value"
          keepZero
          emptyHint="No user activity recorded yet."
        />
        <BreakdownBars
          title={platform ? "Views by Device / Platform (all-time, all platforms)" : "Views by Device / Platform (all-time)"}
          info={
            "Number of video views (plays started), all-time, split by the app they were watched " +
            "on (Web, Roku, Android, iOS, TV...). Not scoped by the date range filter, so it won't " +
            "match Overview's Top Platforms for a selected range. Repeat play-start signals, seeks " +
            "and resumes are not counted as extra views." +
            (platform ? " Always shows every platform, even when a platform filter is selected." : "")
          }
          valueLabel="Views"
          rows={data?.device_breakdown}
          nameKey="device"
          valueKey="count"
          color={TEAL}
          emptyHint="No views recorded yet."
        />
      </div>

      <div className={styled.chartsRow}>
        <FunnelChart
          title="Engagement Funnel (all-time)"
          stages={[
            { label: "Details Page Views", value: funnel.screen_views },
            { label: "Video Starts", value: funnel.plays },
            { label: "Video Completions", value: funnel.completes },
          ]}
          emptyHint="No engagement events recorded yet."
        />
      </div>
    </>
  );
};

export default UsersSection;
